import { useState } from 'react';
import { toast } from 'sonner';
import { X, Copy, Phone, Mail, MapPin, User, CalendarDays, RefreshCw, CheckCircle2, AlertTriangle, Ticket } from 'lucide-react';
import { adminConfirmWinner, adminRedrawWinner } from '@/lib/api';

const copy = (v, label) => {
  if (!v) return;
  navigator.clipboard?.writeText(String(v));
  toast.success(`${label} copied`);
};

const formatDob = (s) => {
  if (!s) return null;
  // Accepts ISO string or YYYY-MM-DD.
  try {
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return s; }
};

const Row = ({ icon: Icon, label, value, copyable, hrefPrefix }) => (
  <div className="flex items-start gap-3 border-b border-pd last:border-0 py-3" data-testid={`winner-row-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <Icon size={16} className="text-[color:var(--pd-gold)] mt-1 shrink-0" />
    <div className="flex-1 min-w-0">
      <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage">{label}</div>
      {value ? (
        hrefPrefix ? (
          <a href={`${hrefPrefix}${value}`} className="text-[color:var(--pd-text)] hover:text-[color:var(--pd-accent-yellow)] break-all">
            {value}
          </a>
        ) : (
          <div className="text-[color:var(--pd-text)] break-all">{value}</div>
        )
      ) : (
        <div className="text-muted-sage italic">Not provided</div>
      )}
    </div>
    {copyable && value ? (
      <button
        onClick={() => copy(value, label)}
        className="text-xs text-muted-sage hover:text-[color:var(--pd-accent-yellow)] inline-flex items-center gap-1 shrink-0"
        aria-label={`Copy ${label}`}
      >
        <Copy size={12} /> Copy
      </button>
    ) : null}
  </div>
);

/**
 * WinnerContactModal
 *
 * Shown immediately after a draw runs (and reopenable from the competitions
 * list for any pending winner). Contains everything the admin needs to reach
 * the winner, plus explicit Confirm / Redraw / Close-for-now actions.
 *
 * Props:
 *  - open (bool)
 *  - draw (object from POST /admin/draw response)
 *  - winner (object from POST /admin/draw response)
 *  - contact (object with full_name/email/phone/address/dob/source/ticket_number)
 *  - competitionTitle (string)
 *  - onConfirmed (callback with confirmed winner)
 *  - onRedrawn (callback with new draw/winner/contact payload)
 *  - onClose (callback for "Close for now")
 */
export const WinnerContactModal = ({ open, draw, winner, contact, competitionTitle, onConfirmed, onRedrawn, onClose }) => {
  const [busy, setBusy] = useState(null); // 'confirm' | 'redraw' | null
  const [confirmingRedraw, setConfirmingRedraw] = useState(false);
  const [redrawReason, setRedrawReason] = useState('uncontactable');

  if (!open || !winner) return null;

  const addressStr = contact?.address
    ? [contact.address.line1, contact.address.line2, contact.address.city, contact.address.postcode]
        .filter(Boolean)
        .join(', ')
    : null;

  const doConfirm = async () => {
    setBusy('confirm');
    try {
      const res = await adminConfirmWinner(winner.id);
      toast.success('Winner confirmed \u2014 celebratory email sent, now public.');
      onConfirmed?.(res.winner);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not confirm');
    } finally { setBusy(null); }
  };

  const doRedraw = async () => {
    setBusy('redraw');
    try {
      const res = await adminRedrawWinner(winner.id, { reason: redrawReason });
      toast.success(`Redraw complete. New winning ticket: ${res.winner.ticket_number}`);
      setConfirmingRedraw(false);
      onRedrawn?.(res); // contains { winner, draw, contact }
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Redraw failed');
    } finally { setBusy(null); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8"
      style={{ height: '100dvh', paddingTop: 'max(1rem, env(safe-area-inset-top))', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
      role="dialog"
      aria-modal="true"
      data-testid="winner-contact-modal"
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-[#0a1611]/85 backdrop-blur-sm" onClick={onClose} aria-hidden />

      <div className="relative w-full max-w-2xl max-h-[92dvh] overflow-y-auto rounded-2xl border border-[color:var(--pd-gold)]/40 bg-[color:var(--pd-surface)] shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-[color:var(--pd-surface)] border-b border-pd px-6 py-4 flex items-start justify-between gap-4">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-[color:var(--pd-gold)]">Pending winner · not yet public</div>
            <h2 className="font-display text-3xl leading-tight mt-1">Winning ticket <span className="text-[color:var(--pd-accent-yellow)]">{winner.ticket_number}</span></h2>
            <p className="text-sm text-muted-sage mt-1">{competitionTitle}</p>
          </div>
          <button onClick={onClose} className="text-muted-sage hover:text-[color:var(--pd-text)] p-1" aria-label="Close" data-testid="winner-modal-close">
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Confidentiality banner */}
          <div className="rounded-xl border border-[color:var(--pd-gold)]/40 bg-[color:var(--pd-gold)]/5 px-4 py-3 flex items-start gap-3 text-sm mb-5">
            <AlertTriangle size={16} className="text-[color:var(--pd-gold)] mt-0.5 shrink-0" />
            <div>
              <strong className="text-[color:var(--pd-text)]">Not yet public. Winner not yet notified.</strong>
              <span className="text-muted-sage"> Pressing &quot;Confirm &amp; send winner email&quot; will (1) publish this winner on the public winners &amp; past-draws pages and (2) immediately email them the celebratory &ldquo;you&rsquo;ve won&rdquo; notification &mdash; this is how the winner finds out. If you don&rsquo;t want to notify them yet, close this modal and come back later. If something looks wrong, press &quot;Redraw&quot; to void this pick and run a fresh draw excluding this ticket.</span>
            </div>
          </div>

          {/* Contact details */}
          <div className="rounded-xl border border-pd bg-[color:var(--pd-bg)] px-4 py-1">
            <Row icon={User} label="Full name" value={contact?.full_name} copyable />
            <Row icon={Mail} label="Email" value={contact?.email} copyable hrefPrefix="mailto:" />
            <Row icon={Phone} label="Phone" value={contact?.phone} copyable hrefPrefix="tel:" />
            <Row icon={MapPin} label="Address" value={addressStr} copyable />
            <Row icon={CalendarDays} label="Date of birth" value={formatDob(contact?.date_of_birth)} copyable />
            <Row icon={Ticket} label="Entry source" value={contact?.source === 'postal' ? 'Free postal entry' : (contact?.source === 'cashflows' || contact?.source === 'nochex' || contact?.source === 'stripe') ? 'Paid ticket' : contact?.source} />
          </div>

          {/* Draw metadata */}
          {draw ? (
            <div className="mt-4 rounded-xl border border-pd bg-[color:var(--pd-bg)] px-4 py-3 text-xs text-muted-sage grid grid-cols-2 gap-y-1" data-testid="winner-modal-draw-meta">
              <div>Draw ID</div>
              <div className="font-mono text-[color:var(--pd-text)] break-all text-right">{draw.id}</div>
              <div>Eligible tickets</div>
              <div className="font-mono text-[color:var(--pd-text)] text-right">{(draw.eligible_count || 0).toLocaleString('en-GB')}</div>
              <div>Seed</div>
              <div className="font-mono text-[color:var(--pd-text)] text-right break-all">{String(draw.seed)}</div>
            </div>
          ) : null}

          {/* Redraw confirmation prompt */}
          {confirmingRedraw ? (
            <div className="mt-5 rounded-xl border border-[color:var(--pd-danger)]/40 bg-[color:var(--pd-danger)]/5 px-4 py-4" data-testid="winner-modal-redraw-prompt">
              <div className="font-display text-lg">Confirm redraw</div>
              <p className="text-sm text-muted-sage mt-1">
                Ticket <span className="text-[color:var(--pd-text)]">{winner.ticket_number}</span> will be permanently voided and a fresh draw will run immediately, excluding this ticket. This action is logged and cannot be undone.
              </p>
              <label className="block mt-3">
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1">Reason (goes on the internal record)</div>
                <select
                  value={redrawReason}
                  onChange={(e) => setRedrawReason(e.target.value)}
                  className="w-full h-10 rounded-lg bg-[color:var(--pd-bg)] border border-pd px-3 text-sm"
                  data-testid="winner-modal-redraw-reason"
                >
                  <option value="uncontactable">Uncontactable after multiple attempts</option>
                  <option value="declined_prize">Winner declined the prize</option>
                  <option value="ineligible">Ineligible under T&amp;Cs (age / duplicate / location)</option>
                  <option value="admin_error">Admin error / test draw</option>
                </select>
              </label>
              <div className="mt-4 flex gap-2">
                <button
                  onClick={doRedraw}
                  disabled={busy === 'redraw'}
                  className="bg-[color:var(--pd-danger)] text-white rounded-xl px-4 py-2 font-medium disabled:opacity-60 inline-flex items-center gap-2"
                  data-testid="winner-modal-redraw-confirm"
                >
                  <RefreshCw size={14} /> {busy === 'redraw' ? 'Redrawing…' : 'Yes, void & redraw'}
                </button>
                <button onClick={() => setConfirmingRedraw(false)} className="border border-pd rounded-xl px-4 py-2 text-sm" disabled={busy === 'redraw'}>Cancel</button>
              </div>
            </div>
          ) : null}
        </div>

        {/* Sticky action bar */}
        {!confirmingRedraw ? (
          <div className="sticky bottom-0 z-10 bg-[color:var(--pd-surface)] border-t border-pd px-6 py-4 flex flex-wrap gap-2 justify-end">
            <button
              onClick={onClose}
              className="border border-pd rounded-xl px-4 py-2.5 text-sm text-muted-sage hover:text-[color:var(--pd-text)]"
              data-testid="winner-modal-close-later"
            >
              Close for now
            </button>
            <button
              onClick={() => setConfirmingRedraw(true)}
              className="border border-[color:var(--pd-danger)]/60 text-[color:var(--pd-danger)] rounded-xl px-4 py-2.5 text-sm inline-flex items-center gap-2 hover:bg-[color:var(--pd-danger)]/10"
              data-testid="winner-modal-redraw"
            >
              <RefreshCw size={14} /> Redraw
            </button>
            <button
              onClick={doConfirm}
              disabled={busy === 'confirm'}
              className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-2.5 font-medium inline-flex items-center gap-2 disabled:opacity-60"
              data-testid="winner-modal-confirm"
              title="Publish this winner publicly AND send them the celebratory 'you've won' email"
            >
              <CheckCircle2 size={14} /> {busy === 'confirm' ? 'Sending\u2026' : 'Confirm & send winner email'}
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default WinnerContactModal;
